import { useEffect, type ReactNode } from "react";
import { EditorContent, useEditor, useEditorState, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { TableKit } from "@tiptap/extension-table";
import { Button, cx } from "./ui";

/**
 * The post body editor.
 *
 * Produces HTML, which the API sanitises against an allowlist before it is
 * stored — so nothing here is a security control. The toolbar is bounded by
 * that allowlist rather than by taste: offering a colour picker whose output
 * gets stripped on save would be worse than not offering it, since the author
 * would watch their formatting vanish with no explanation.
 *
 * Within that bound it now offers everything the allowlist actually keeps —
 * headings, the four emphasis marks, both list kinds, quotes, inline code and
 * code blocks, links, images, rules and tables — plus undo, redo and a way out
 * of formatting you did not mean to apply.
 *
 * Keep this in step with ALLOWED_TAGS in the API's sanitize.ts. A button added
 * here without a tag there is a button that silently does nothing.
 */

const HEADINGS = [2, 3, 4] as const;

/** What the block dropdown offers, in the order it offers it. */
const BLOCKS = [
  { value: "paragraph", label: "Body text" },
  { value: "h2", label: "Heading" },
  { value: "h3", label: "Subheading" },
  { value: "h4", label: "Small heading" },
  { value: "codeBlock", label: "Code block" },
] as const;

type BlockValue = (typeof BLOCKS)[number]["value"];

export function RichTextEditor({
  value,
  onChange,
  onRequestImage,
}: {
  value: string;
  onChange: (html: string) => void;
  /** Opens the media picker; resolves to a URL, or null if cancelled. */
  onRequestImage: () => Promise<{ src: string; alt: string } | null>;
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // h1 belongs to the page, not to a paragraph inside it — a post that
        // can mint its own h1 breaks the document outline on every page it
        // appears in.
        heading: { levels: [...HEADINGS] },
        // StarterKit ships its own link; configured here rather than added as a
        // second extension, which registers the name twice and makes which one
        // handles a paste a matter of load order.
        link: {
          openOnClick: false,
          autolink: true,
          protocols: ["http", "https", "mailto", "tel"],
        },
      }),
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({
        placeholder: "Write the post…",
        // Only the first empty paragraph, not every one. Prompting on every
        // blank line turns a half-written draft into a wall of grey hints.
        showOnlyWhenEditable: true,
      }),
      // `resizable` is off: column widths are stored as inline styles, and
      // `style` is not on the allowlist — the widths would be dragged, saved,
      // stripped, and the table would spring back with no explanation.
      TableKit.configure({ table: { resizable: false } }),
    ],
    content: value,
    // Rendering the editor during SSR gives React a tree the client then
    // disagrees with; the admin is a SPA, but this keeps the warning away.
    immediatelyRender: false,
    onUpdate: ({ editor: instance }) => onChange(instance.getHTML()),
    editorProps: {
      attributes: {
        class: "prose-editor",
      },
    },
  });

  // Content arriving after mount — a post loaded from the API, or a draft
  // reset — has to be pushed in, but only when it genuinely differs, or every
  // keystroke would send the cursor back to the start.
  useEffect(() => {
    if (editor === null) return;
    if (value === editor.getHTML()) return;
    editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);

  if (editor === null) return <div className="p-4 text-sm text-dim">Loading the editor…</div>;

  return (
    <div className="rounded-md border border-hairline-strong bg-base">
      <Toolbar editor={editor} onRequestImage={onRequestImage} />
      <EditorContent editor={editor} />
      <WordCount editor={editor} />
    </div>
  );
}

/**
 * The toolbar, as its own component so that it only ever mounts with a real
 * editor. `useEditorState` subscribes to the instance it is handed; handed null
 * on the first render — which is what `immediatelyRender: false` guarantees —
 * it has nothing to subscribe to and never starts.
 */
function Toolbar({
  editor,
  onRequestImage,
}: {
  editor: Editor;
  onRequestImage: () => Promise<{ src: string; alt: string } | null>;
}) {
  /**
   * Everything the toolbar draws itself from.
   *
   * Through `useEditorState` rather than by reading the editor during render:
   * as of Tiptap 3 `useEditor` does not re-render on a transaction, so a
   * toolbar that calls `editor.isActive(...)` inline is one whose highlighting
   * is frozen at whatever was true when it mounted. Nothing about that failure
   * is visible in the markup — the buttons still work, they just stop telling
   * you what is on.
   */
  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      block: currentBlock(current),
      bold: current.isActive("bold"),
      italic: current.isActive("italic"),
      underline: current.isActive("underline"),
      strike: current.isActive("strike"),
      code: current.isActive("code"),
      bulletList: current.isActive("bulletList"),
      orderedList: current.isActive("orderedList"),
      blockquote: current.isActive("blockquote"),
      link: current.isActive("link"),
      inTable: current.isActive("table"),
      canUndo: current.can().undo(),
      canRedo: current.can().redo(),
    }),
  });

  const setBlock = (block: BlockValue) => {
    const chain = editor.chain().focus();
    if (block === "paragraph") chain.setParagraph().run();
    else if (block === "codeBlock") chain.setCodeBlock().run();
    else chain.setHeading({ level: Number(block.slice(1)) as 2 | 3 | 4 }).run();
  };

  const addLink = () => {
    const previous = editor.getAttributes("link")["href"] as string | undefined;
    const href = window.prompt("Link address", previous ?? "https://");
    if (href === null) return;
    if (href.trim() === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: href.trim() }).run();
  };

  const addImage = async () => {
    const picked = await onRequestImage();
    if (picked === null) return;
    editor.chain().focus().setImage({ src: picked.src, alt: picked.alt }).run();
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-1 border-b border-hairline p-2">
        <Tool
          label="Undo"
          disabled={!state.canUndo}
          onClick={() => editor.chain().focus().undo().run()}
        />
        <Tool
          label="Redo"
          disabled={!state.canRedo}
          onClick={() => editor.chain().focus().redo().run()}
        />

        <Divider />

        {/* A dropdown rather than five buttons: these are mutually exclusive,
            and a row of toggles where only one can ever be on is a radio group
            wearing the wrong clothes. */}
        <select
          value={state.block}
          onChange={(event) => setBlock(event.target.value as BlockValue)}
          aria-label="Block type"
          className={cx(
            "rounded-md border border-hairline-strong bg-raised px-2 py-1.5 text-sm text-ink",
            "focus:border-accent",
          )}
        >
          {BLOCKS.map((block) => (
            <option key={block.value} value={block.value}>
              {block.label}
            </option>
          ))}
        </select>

        <Divider />

        <Tool
          label="Bold"
          active={state.bold}
          onClick={() => editor.chain().focus().toggleMark("bold").run()}
        />
        <Tool
          label="Italic"
          active={state.italic}
          onClick={() => editor.chain().focus().toggleMark("italic").run()}
        />
        <Tool
          label="Underline"
          active={state.underline}
          onClick={() => editor.chain().focus().toggleMark("underline").run()}
        />
        <Tool
          label="Strike"
          active={state.strike}
          onClick={() => editor.chain().focus().toggleMark("strike").run()}
        />
        <Tool
          label="Code"
          active={state.code}
          onClick={() => editor.chain().focus().toggleMark("code").run()}
        />

        <Divider />

        <Tool
          label="Bullets"
          active={state.bulletList}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <Tool
          label="Numbers"
          active={state.orderedList}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <Tool
          label="Quote"
          active={state.blockquote}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        />

        <Divider />

        <Tool
          label="Link"
          active={state.link}
          onClick={addLink}
        />
        <Tool
          label="Unlink"
          disabled={!state.link}
          onClick={() => editor.chain().focus().extendMarkRange("link").unsetLink().run()}
        />

        <Divider />

        <Tool label="Image" onClick={() => void addImage()} />
        <Tool
          label="Table"
          disabled={state.inTable}
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run()
          }
        />
        <Tool
          label="Rule"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        />

        <Divider />

        {/* Escape hatch. Every editor eventually gets pasted something wearing
            formatting nobody chose, and hunting for which toggles are on is a
            worse job than starting the paragraph again. */}
        <Tool
          label="Clear"
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        />
      </div>

      {/* Table controls appear only inside a table. Six buttons that do nothing
          most of the time are six buttons in the way most of the time. */}
      {state.inTable && (
        <div className="flex flex-wrap items-center gap-1 border-b border-hairline bg-surface/60 px-2 py-1.5">
          <span className="mr-1 text-[0.6875rem] uppercase tracking-[0.12em] text-dim">
            Table
          </span>
          <Tool label="+ Row" onClick={() => editor.chain().focus().addRowAfter().run()} />
          <Tool label="+ Column" onClick={() => editor.chain().focus().addColumnAfter().run()} />
          <Tool label="− Row" onClick={() => editor.chain().focus().deleteRow().run()} />
          <Tool label="− Column" onClick={() => editor.chain().focus().deleteColumn().run()} />
          <Tool
            label="Header row"
            onClick={() => editor.chain().focus().toggleHeaderRow().run()}
          />
          <Tool
            label="Delete table"
            onClick={() => editor.chain().focus().deleteTable().run()}
          />
        </div>
      )}
    </>
  );
}

/**
 * The same count the API works the reading time out from, shown while it can
 * still change what you write rather than after you have saved.
 */
function WordCount({ editor }: { editor: Editor }) {
  const words = useEditorState({ editor, selector: ({ editor: current }) => wordsIn(current) });

  return (
    <div className="flex items-center justify-between border-t border-hairline px-3 py-1.5 text-xs text-dim">
      <span>{words} words</span>
      <span className="tabular">~{Math.max(1, Math.round(words / 200))} min read</span>
    </div>
  );
}

/** Which entry the block dropdown should be showing for the current cursor. */
function currentBlock(editor: Editor): BlockValue {
  if (editor.isActive("codeBlock")) return "codeBlock";
  for (const level of HEADINGS) {
    if (editor.isActive("heading", { level })) return `h${level}` as BlockValue;
  }
  return "paragraph";
}

function wordsIn(editor: Editor): number {
  const text = editor.getText().trim();
  return text === "" ? 0 : text.split(/\s+/).length;
}

function Divider() {
  return <span className="mx-1 self-stretch border-l border-hairline" aria-hidden="true" />;
}

/**
 * One toolbar button.
 *
 * `aria-pressed` rather than a colour alone: a toolbar whose only signal that
 * bold is on is a background tint is a toolbar a screen reader user cannot read
 * the state of.
 */
function Tool({
  label,
  active,
  disabled = false,
  onClick,
}: {
  label: ReactNode;
  /**
   * Present for a toggle, absent for a one-shot action. The distinction is the
   * whole reason this is optional rather than defaulted: a toggle must report
   * `aria-pressed` in both states, or it does not announce as a toggle at all,
   * while putting `aria-pressed="false"` on Undo would claim it is one.
   */
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant={active === true ? "primary" : "ghost"}
      disabled={disabled}
      {...(active === undefined ? {} : { "aria-pressed": active })}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}
